import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as DB from '@/lib/supabaseDb';
import { useQuickAction } from '@/hooks/useQuickAction';
import { useDataRefresh } from '@/hooks/useDataRefresh';
import { useToast } from '@/components/feature/Toast';
import { useDebounce } from '@/hooks/useDebounce';
import CustomerEditModal from '@/components/feature/CustomerEditModal';
import { formatTRY } from '@/lib/currencyFormat';
import Badge from '@/components/base/Badge';
import { customerStatus } from '@/lib/statusDefs';

interface Customer {
  id: string;
  full_name: string;
  phone: string;
  second_phone: string;
  address: string;
  city: string;
  district: string;
  identity_number: string;
  notes: string;
  status: string;
  total_sales_amount: number;
  total_paid_amount: number;
  total_debt_amount: number;
  last_purchase_date: string;
  created_at: string;
  customer_no: string;
  phone_normalized: string;
}

type SortKey = 'full_name' | 'total_debt_amount' | 'last_purchase_date' | 'total_sales_amount' | 'created_at';
type SortDir = 'asc' | 'desc';

export default function CustomersPage() {
  const navigate = useNavigate();
  const { openModal } = useQuickAction();
  const { version } = useDataRefresh();
  const { showToast } = useToast();
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);
  const [searchParams] = useSearchParams();

  // ── Arama ve filtre state ────────────────────────────
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [statusFilter, setStatusFilter] = useState<string>('Tümü');
  const [districtFilter, setDistrictFilter] = useState<string>('Tümü');
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  // ── Veri state ────────────────────────────────────────
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Arama/filtre değiştiğinde sayfayı 1'e döndür
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, districtFilter]);

  // Veri çekme — server-side search + pagination
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    DB.searchCustomers(
      debouncedSearch,
      { status: statusFilter !== 'Tümü' ? statusFilter : undefined, district: districtFilter !== 'Tümü' ? districtFilter : undefined },
      currentPage,
      pageSize,
    ).then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) {
        console.error('[CustomersPage] searchCustomers hatası:', err);
        setError(err);
        setLoading(false);
        return;
      }
      console.debug('[CustomersPage] searchCustomers sonucu:', { count: data?.customers?.length, total: data?.total });
      setCustomers((data?.customers || []) as Customer[]);
      setTotal(data?.total || 0);
      setError(null);
      setLoading(false);
    }).catch((e) => {
      console.error('[CustomersPage] searchCustomers exception:', e);
      if (!cancelled) { setError('Veri yüklenirken hata oluştu.'); setLoading(false); }
    });

    return () => { cancelled = true; };
  }, [debouncedSearch, statusFilter, districtFilter, currentPage, refreshKey, version]);

  // ── İlçe listesi (sunucudan gelen veriye göre) ────────
  const districts = useMemo(() => {
    // District filtresi için sabit seçenekler (veritabanında ne varsa gösterilecek)
    // İlk yüklemede tüm müşterileri getirip ilçe listesini çıkarmak yerine
    // sabit bir liste kullanalım, filtresiz arama yapıldığında zaten tüm ilçeler gelir
    const d = new Set((customers || []).map(c => c.district).filter(Boolean));
    return ['Tümü', ...Array.from(d).sort()];
  }, [customers]);

  const statuses = ['Tümü', 'Aktif', 'Borçlu', 'Borcu Yok', 'İcrada', 'Kara Liste'];

  // ── Client-side sort (sadece mevcut sayfadaki kayıtlar) ──
  const sorted = useMemo(() => {
    const result = [...(customers || [])];
    result.sort((a, b) => {
      const valA = a[sortKey];
      const valB = b[sortKey];
      if (typeof valA === 'string' && typeof valB === 'string') {
        return sortDir === 'asc' ? valA.localeCompare(valB, 'tr') : valB.localeCompare(valA, 'tr');
      }
      const numA = Number(valA ?? 0);
      const numB = Number(valB ?? 0);
      return sortDir === 'asc' ? numA - numB : numB - numA;
    });
    return result;
  }, [customers, sortKey, sortDir]);

  const totalPages = Math.ceil(total / pageSize);

  // ── Silme ve düzenleme state ──────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const handleDelete = async () => {
    if (!showDeleteModal || deleting) return;
    setDeleting(true);
    const { error: err } = await DB.deleteCustomer(showDeleteModal);
    if (err) { showToast('Silme başarısız: ' + err, 'error'); setDeleting(false); return; }
    showToast('Müşteri başarıyla silindi.', 'success');
    setShowDeleteModal(null);
    setDeleting(false);
    refresh();
  };

  const sortIcon = (key: SortKey) => {
    if (sortKey !== key) return <i className="ri-arrow-up-down-line text-foreground-300 text-xs"></i>;
    return <i className={`text-xs text-primary-500 ${sortDir === 'asc' ? 'ri-sort-asc' : 'ri-sort-desc'}`}></i>;
  };

  const cleanPhone = (phone: string) => (phone || '').replace(/\s/g, '');

  const handleSearchChange = (value: string) => {
    setSearch(value);
    // currentPage reset'i useEffect ile otomatik
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
  };

  const handleDistrictChange = (value: string) => {
    setDistrictFilter(value);
  };

  // ── Loading ────────────────────────────────────────────
  if (loading && customers.length === 0) {
    return (
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div><h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Müşteriler</h2><p className="text-sm text-foreground-400 mt-0.5">Yükleniyor...</p></div>
        </div>
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></div>
          <p className="text-sm text-foreground-400">Müşteriler yükleniyor...</p>
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────
  if (error && customers.length === 0) {
    return (
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div><h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Müşteriler</h2></div>
        </div>
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center">
            <i className="ri-error-warning-line text-2xl text-red-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-foreground-700">Yükleme başarısız</h3>
          <p className="text-sm text-foreground-400 max-w-sm text-center">{error}</p>
          <button className="btn-primary flex items-center gap-2 mt-2 cursor-pointer whitespace-nowrap" onClick={refresh}>
            <i className="ri-refresh-line"></i><span>Tekrar Dene</span>
          </button>
        </div>
      </div>
    );
  }

  // ── Boş durum ──────────────────────────────────────────
  if (customers.length === 0 && !loading && !error) {
    const hasFilters = debouncedSearch || statusFilter !== 'Tümü' || districtFilter !== 'Tümü';
    return (
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Müşteriler</h2>
            <p className="text-sm text-foreground-400 mt-0.5">Toplam {total} müşteri</p>
          </div>
          <button className="btn-primary flex items-center gap-2 self-start cursor-pointer whitespace-nowrap" onClick={() => openModal('customer')}>
            <i className="ri-user-add-line"></i>
            <span>Yeni Müşteri</span>
          </button>
        </div>

        {/* Arama ve filtreler */}
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <input type="text" placeholder="İsim, telefon, müşteri no, TC veya ilçe ile ara..." value={search} onChange={(e) => handleSearchChange(e.target.value)} className="w-full pl-10 pr-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700 placeholder:text-foreground-300" />
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-300 text-sm"></i>
          </div>
          <select value={statusFilter} onChange={(e) => handleStatusChange(e.target.value)} className="px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer">
            {statuses.map(s => <option key={s} value={s}>Durum: {s}</option>)}
          </select>
          <select value={districtFilter} onChange={(e) => handleDistrictChange(e.target.value)} className="px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer">
            {districts.map(d => <option key={d} value={d}>İlçe: {d}</option>)}
          </select>
        </div>

        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center">
            {hasFilters
              ? <i className="ri-user-search-line text-3xl text-foreground-300"></i>
              : <i className="ri-user-add-line text-3xl text-foreground-300"></i>
            }
          </div>
          <h3 className="text-lg font-semibold text-foreground-700">
            {hasFilters ? 'Aramanızla eşleşen müşteri bulunamadı' : 'Henüz müşteri kaydı yok'}
          </h3>
          <p className="text-sm text-foreground-400 max-w-sm text-center">
            {hasFilters ? 'Filtreleri değiştirerek tekrar deneyin.' : 'İlk müşteri kaydını oluşturmak için "Yeni Müşteri" butonunu kullanabilirsiniz.'}
          </p>
          {hasFilters ? (
            <button className="btn-outline flex items-center gap-2 mt-2 cursor-pointer whitespace-nowrap" onClick={() => { setSearch(''); setStatusFilter('Tümü'); setDistrictFilter('Tümü'); }}>
              <i className="ri-close-line"></i>
              <span>Filtreleri Temizle</span>
            </button>
          ) : (
            <button className="btn-primary flex items-center gap-2 mt-2 cursor-pointer whitespace-nowrap" onClick={() => openModal('customer')}>
              <i className="ri-user-add-line"></i>
              <span>Yeni Müşteri</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Ana render ──────────────────────────────────────────
  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Müşteriler</h2>
          <p className="text-sm text-foreground-400 mt-0.5">Toplam {total} müşteri</p>
        </div>
        <button className="btn-primary flex items-center gap-2 self-start cursor-pointer whitespace-nowrap" onClick={() => openModal('customer')}>
          <i className="ri-user-add-line"></i>
          <span>Yeni Müşteri</span>
        </button>
      </div>

      {/* Arama ve filtreler */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <input type="text" placeholder="İsim, telefon, müşteri no, TC veya ilçe ile ara..." value={search} onChange={(e) => handleSearchChange(e.target.value)} className="w-full pl-10 pr-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700 placeholder:text-foreground-300" />
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-300 text-sm"></i>
          {loading && (
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></div>
            </div>
          )}
        </div>
        <select value={statusFilter} onChange={(e) => handleStatusChange(e.target.value)} className="px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer">
          {statuses.map(s => <option key={s} value={s}>Durum: {s}</option>)}
        </select>
        <select value={districtFilter} onChange={(e) => handleDistrictChange(e.target.value)} className="px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer">
          {districts.map(d => <option key={d} value={d}>İlçe: {d}</option>)}
        </select>
      </div>

      {/* Tablo */}
      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full min-w-[1200px]">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/50">
                <th className="table-header">Müşteri No</th>
                <th className="table-header cursor-pointer" onClick={() => handleSort('full_name')}><span className="flex items-center gap-1">Müşteri {sortIcon('full_name')}</span></th>
                <th className="table-header">Telefon</th>
                <th className="table-header">İlçe</th>
                <th className="table-header cursor-pointer" onClick={() => handleSort('total_sales_amount')}><span className="flex items-center gap-1">Toplam Alışveriş {sortIcon('total_sales_amount')}</span></th>
                <th className="table-header cursor-pointer" onClick={() => handleSort('total_debt_amount')}><span className="flex items-center gap-1">Kalan Borç {sortIcon('total_debt_amount')}</span></th>
                <th className="table-header cursor-pointer" onClick={() => handleSort('last_purchase_date')}><span className="flex items-center gap-1">Son Alışveriş {sortIcon('last_purchase_date')}</span></th>
                <th className="table-header">Durum</th>
                <th className="table-header text-center">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {(sorted || []).map((customer) => (
                <tr key={customer.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                  <td className="table-cell font-mono text-xs text-foreground-500">{customer.customer_no || '—'}</td>
                  <td className="table-cell">
                    <button onClick={() => navigate(`/customers/${customer.id}`)} className="text-left hover:text-primary-500 transition-colors cursor-pointer">
                      <span className="font-medium text-foreground-800">{customer.full_name}</span>
                      {customer.notes && <p className="text-xs text-foreground-400 mt-0.5 truncate max-w-[200px]">{customer.notes.slice(0, 50)}</p>}
                    </button>
                  </td>
                  <td className="table-cell">
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-foreground-600 text-sm">{customer.phone}</span>
                        <a href={`tel:${cleanPhone(customer.phone)}`} className="w-6 h-6 rounded-md bg-emerald-50 flex items-center justify-center cursor-pointer hover:bg-emerald-100 transition-colors flex-shrink-0" title="Ara">
                          <i className="ri-phone-line text-emerald-600 text-[10px]"></i>
                        </a>
                        <a href={`https://wa.me/90${cleanPhone(customer.phone).slice(1)}`} target="_blank" rel="noopener noreferrer" className="w-6 h-6 rounded-md bg-emerald-50 flex items-center justify-center cursor-pointer hover:bg-emerald-100 transition-colors flex-shrink-0" title="WhatsApp">
                          <i className="ri-whatsapp-line text-emerald-600 text-[10px]"></i>
                        </a>
                      </div>
                      {customer.second_phone && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-foreground-400 text-xs">{customer.second_phone}</span>
                          <a href={`tel:${cleanPhone(customer.second_phone)}`} className="w-5 h-5 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-emerald-50 transition-colors flex-shrink-0" title="2. Telefonu Ara">
                            <i className="ri-phone-line text-foreground-400 text-[9px]"></i>
                          </a>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="table-cell text-foreground-600">{customer.district}</td>
                  <td className="table-cell font-medium">{formatTRY(customer.total_sales_amount || 0)}</td>
                  <td className="table-cell">
                    {customer.total_debt_amount > 0 ? <span className="text-red-500 font-medium">{formatTRY(customer.total_debt_amount)}</span> : <span className="text-emerald-600 text-xs">—</span>}
                  </td>
                  <td className="table-cell text-foreground-500">{customer.last_purchase_date ? new Date(customer.last_purchase_date).toLocaleDateString('tr-TR') : '—'}</td>
                  <td className="table-cell py-2"><Badge badgeClass={customerStatus.badgeClass(customer.status)} icon={customerStatus.icon(customer.status)}>{customer.status}</Badge></td>
                  <td className="table-cell py-2">
                    <div className="flex items-center justify-center gap-1.5">
                      <button onClick={() => navigate(`/customers/${customer.id}`)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-primary-100 hover:text-primary-600 transition-colors" title="Görüntüle"><i className="ri-eye-line text-sm"></i></button>
                      <button onClick={() => setShowEditModal(customer.id)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-accent-100 hover:text-accent-700 transition-colors" title="Düzenle"><i className="ri-edit-line text-sm"></i></button>
                      <button onClick={() => setShowDeleteModal(customer.id)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-red-50 hover:text-red-500 transition-colors" title="Sil"><i className="ri-delete-bin-line text-sm"></i></button>
                    </div>
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && !loading && (
                <tr><td colSpan={9} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-user-search-line text-2xl text-foreground-300"></i></div>
                    <p className="text-sm font-medium text-foreground-500">Müşteri bulunamadı</p>
                    <p className="text-xs text-foreground-400">Filtreleri değiştirerek tekrar deneyin</p>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden p-3 space-y-2.5">
          {(sorted || []).map((customer) => (
            <div key={customer.id} className="bg-background-50 border border-background-200/70 rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-foreground-400">{customer.customer_no || '—'}</span>
                  <Badge badgeClass={customerStatus.badgeClass(customer.status)} icon={customerStatus.icon(customer.status)}>{customer.status}</Badge>
                </div>
                <div className="flex items-center gap-1">
                  <a href={`tel:${cleanPhone(customer.phone)}`} className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center cursor-pointer"><i className="ri-phone-line text-emerald-600 text-xs"></i></a>
                  <a href={`https://wa.me/90${cleanPhone(customer.phone).slice(1)}`} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center cursor-pointer"><i className="ri-whatsapp-line text-emerald-600 text-xs"></i></a>
                </div>
              </div>
              <button onClick={() => navigate(`/customers/${customer.id}`)} className="text-sm font-medium text-foreground-800 cursor-pointer hover:text-primary-500 block text-left">
                {customer.full_name}
              </button>
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-xs text-foreground-500">{customer.phone}</span>
                  <span className="text-[11px] text-foreground-400">{customer.district}</span>
                </div>
                <div className="text-right">
                  {customer.total_debt_amount > 0 ? (
                    <span className="text-sm font-semibold text-red-500">{formatTRY(customer.total_debt_amount)}</span>
                  ) : (
                    <span className="text-xs text-emerald-600">Borç yok</span>
                  )}
                  <p className="text-[11px] text-foreground-400">Toplam: {formatTRY(customer.total_sales_amount || 0)}</p>
                </div>
              </div>
              <div className="flex items-center justify-between pt-1 border-t border-background-100">
                <span className="text-[11px] text-foreground-400">
                  {customer.last_purchase_date ? `Son: ${new Date(customer.last_purchase_date).toLocaleDateString('tr-TR')}` : ''}
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => navigate(`/customers/${customer.id}`)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-primary-100 hover:text-primary-600"><i className="ri-eye-line text-xs"></i></button>
                  <button onClick={() => setShowEditModal(customer.id)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-accent-100 hover:text-accent-700"><i className="ri-edit-line text-xs"></i></button>
                  <button onClick={() => setShowDeleteModal(customer.id)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-red-50 hover:text-red-500"><i className="ri-delete-bin-line text-xs"></i></button>
                </div>
              </div>
            </div>
          ))}
          {sorted.length === 0 && !loading && (
            <div className="flex flex-col items-center gap-3 py-10">
              <div className="w-12 h-12 rounded-xl bg-background-100 flex items-center justify-center"><i className="ri-user-search-line text-xl text-foreground-300"></i></div>
              <p className="text-sm text-foreground-400">Müşteri bulunamadı</p>
            </div>
          )}
        </div>

        {/* Pagination — server-side totalPages */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-background-200">
            <span className="text-xs text-foreground-400">Sayfa {currentPage} / {totalPages} ({total} kayıt)</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed"><i className="ri-arrow-left-s-line text-sm"></i></button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 7) pageNum = i + 1;
                else if (currentPage <= 4) pageNum = i + 1;
                else if (currentPage >= totalPages - 3) pageNum = totalPages - 6 + i;
                else pageNum = currentPage - 3 + i;
                return <button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={`w-8 h-8 rounded-lg text-sm font-medium cursor-pointer transition-colors ${pageNum === currentPage ? 'bg-primary-500 text-background-50' : 'hover:bg-background-100 text-foreground-600'}`}>{pageNum}</button>;
              })}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed"><i className="ri-arrow-right-s-line text-sm"></i></button>
            </div>
          </div>
        )}
      </div>

      {/* Silme Modalı */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4" onClick={() => setShowDeleteModal(null)}>
          <div className="bg-background-50 rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4 mx-auto"><i className="ri-error-warning-line text-xl text-red-500"></i></div>
            <h3 className="text-lg font-semibold text-foreground-900 text-center mb-2">Müşteriyi Sil</h3>
            <p className="text-sm text-foreground-500 text-center mb-6">Bu müşteri ve ilişkili tüm kayıtlar kalıcı olarak silinecek. Bu işlem geri alınamaz.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteModal(null)} disabled={deleting} className="flex-1 btn-outline text-center cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
                {deleting ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomerEditModal
        isOpen={!!showEditModal}
        onClose={() => { setShowEditModal(null); refresh(); }}
        customerId={showEditModal || ''}
      />
    </div>
  );
}